/**
 * North Carolina cities → regions.
 *
 * NC has ~550 incorporated municipalities. We don't list them all — this
 * covers the ~55 where the Indian community actually lives. Anything typed
 * that isn't here resolves to "Elsewhere in NC", so no city is ever rejected.
 *
 * To promote a town: add one line. Nothing else changes.
 */

export const REGIONS = {
  triangle:  { label: "Triangle",         color: "blue"   },
  charlotte: { label: "Charlotte Metro",  color: "coral"  },
  triad:     { label: "Triad",            color: "teal"   },
  coastal:   { label: "Coastal",          color: "purple" },
  mountains: { label: "Mountains",        color: "green"  },
  other:     { label: "Elsewhere in NC",  color: "gray"   },
};

export const REGION_ORDER = ["triangle", "charlotte", "triad", "coastal", "mountains", "other"];

// City → region key
export const CITY_REGION = {
  // ── Triangle ──────────────────────────────────────────────
  "Raleigh": "triangle",
  "Cary": "triangle",
  "Durham": "triangle",
  "Chapel Hill": "triangle",
  "Morrisville": "triangle",
  "Apex": "triangle",
  "Holly Springs": "triangle",
  "Fuquay-Varina": "triangle",
  "Wake Forest": "triangle",
  "Garner": "triangle",
  "Clayton": "triangle",
  "Knightdale": "triangle",
  "Wendell": "triangle",
  "Zebulon": "triangle",
  "Carrboro": "triangle",
  "Hillsborough": "triangle",
  "Pittsboro": "triangle",
  "Research Triangle Park": "triangle",
  "Youngsville": "triangle",
  "Rolesville": "triangle",
  "Sanford": "triangle",
  "Smithfield": "triangle",

  // ── Charlotte Metro ───────────────────────────────────────
  "Charlotte": "charlotte",
  "Huntersville": "charlotte",
  "Matthews": "charlotte",
  "Concord": "charlotte",
  "Cornelius": "charlotte",
  "Davidson": "charlotte",
  "Mooresville": "charlotte",
  "Waxhaw": "charlotte",
  "Indian Trail": "charlotte",
  "Mint Hill": "charlotte",
  "Pineville": "charlotte",
  "Kannapolis": "charlotte",
  "Gastonia": "charlotte",
  "Belmont": "charlotte",
  "Harrisburg": "charlotte",
  "Monroe": "charlotte",

  // ── Triad ─────────────────────────────────────────────────
  "Greensboro": "triad",
  "Winston-Salem": "triad",
  "High Point": "triad",
  "Burlington": "triad",
  "Kernersville": "triad",
  "Clemmons": "triad",
  "Oak Ridge": "triad",
  "Summerfield": "triad",
  "Thomasville": "triad",
  "Asheboro": "triad",
  "Graham": "triad",

  // ── Coastal ───────────────────────────────────────────────
  "Wilmington": "coastal",
  "Greenville": "coastal",
  "Jacksonville": "coastal",
  "New Bern": "coastal",
  "Leland": "coastal",
  "Morehead City": "coastal",
  "Elizabeth City": "coastal",
  "Rocky Mount": "coastal",
  "Wilson": "coastal",
  "Goldsboro": "coastal",

  // ── Mountains ─────────────────────────────────────────────
  "Asheville": "mountains",
  "Boone": "mountains",
  "Hickory": "mountains",
  "Hendersonville": "mountains",
  "Morganton": "mountains",
  "Statesville": "mountains",
  "Lenoir": "mountains",

  // ── Other notable ─────────────────────────────────────────
  "Fayetteville": "other",
  "Pinehurst": "other",
  "Southern Pines": "other",
  "Lumberton": "other",
  "Salisbury": "other",
};

// Alphabetical list for the combobox
export const NC_CITIES = Object.keys(CITY_REGION).sort((a, b) => a.localeCompare(b));

/** Resolve any city string to a region key. Unknown cities → "other". */
export function regionForCity(city) {
  if (!city) return "other";
  const exact = CITY_REGION[city];
  if (exact) return exact;

  // Tolerate casing and punctuation differences ("winston salem" → Winston-Salem)
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  const target = norm(city);
  for (const known of Object.keys(CITY_REGION)) {
    if (norm(known) === target) return CITY_REGION[known];
  }
  return "other";
}

export function regionLabel(city) {
  return REGIONS[regionForCity(city)].label;
}

/** Cities grouped by region, for grouped rendering in the combobox. */
export function citiesByRegion() {
  const groups = {};
  for (const key of REGION_ORDER) groups[key] = [];
  for (const [city, key] of Object.entries(CITY_REGION)) groups[key].push(city);
  for (const key of REGION_ORDER) groups[key].sort((a, b) => a.localeCompare(b));
  return groups;
}

/** Fuzzy-ish search: prefix matches first, then substring. */
export function searchCities(query, limit = 8) {
  const q = query.trim().toLowerCase();
  if (!q) return NC_CITIES.slice(0, limit);
  const starts = [], contains = [];
  for (const city of NC_CITIES) {
    const c = city.toLowerCase();
    if (c.startsWith(q)) starts.push(city);
    else if (c.includes(q)) contains.push(city);
  }
  return [...starts, ...contains].slice(0, limit);
}
